import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../shared/i18n';
import { FaInstagram, FaFacebookF, FaTiktok, FaWhatsapp } from 'react-icons/fa';
import { IoGameController } from 'react-icons/io5';
import { ResourcesSection } from '../shared/components';
import LinksSection from '../shared/components/LinksSection';
import conectadosLogo from '../assets/conectados/logo.png';
import fortiGamerImage from '../assets/juega-con-forti/forti-gamer.png';
import * as S from './HomePage.styles';

function HomePage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [isGamesSectionVisible, setIsGamesSectionVisible] = useState(false);
  const gamesSectionRef = useRef(null);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsGamesSectionVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.3, // Se considera visible cuando al menos el 30% está en viewport
        rootMargin: '0px',
      }
    );

    const currentSection = gamesSectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);
  
  const scrollToGames = () => {
    const gamesSection = document.getElementById('games-section');
    if (gamesSection) {
      gamesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFloatingButtonClick = () => {
    navigate('/juegaconforti');
  };
  
  return (
    <S.PageContainer>
      {/* Hero Section */}
      <S.HeroSection>
        <S.HeroBackground $offset={scrollY} />
        <S.HeroContent>
          <S.HeroTitle>{t('home.title')}</S.HeroTitle>
          <S.HeroSubtitle>
            {t('home.heroSubtitle')}
          </S.HeroSubtitle>
          <S.CTAButton onClick={scrollToGames}>
            {t('home.exploreGames')}
          </S.CTAButton>
        </S.HeroContent>
        <S.ScrollIndicator onClick={scrollToGames}>
          <S.ScrollArrow />
        </S.ScrollIndicator>
      </S.HeroSection>

      {/* Games Section */}
      <S.Section id="games-section" ref={gamesSectionRef} background="white">
        <S.SectionTitle>{t('home.ourGames')}</S.SectionTitle>
        <S.SectionSubtitle>
          {t('home.subtitle')}
        </S.SectionSubtitle>
        
        <S.GamesGrid>
          <S.GameCard as={Link} to="/conectados">
            <S.GameImageContainer>
              <S.GameImage 
                src={conectadosLogo} 
                alt={t('conectados.title')}
              />
            </S.GameImageContainer>
            <S.GameInfo>
              <S.GameTitle>{t('conectados.title')}</S.GameTitle>
              <S.GameDescription>
                {t('conectados.subtitle')}. {t('home.conectadosDescription')}
              </S.GameDescription>
            </S.GameInfo>
            <S.GameBadge>
              <IoGameController />
            </S.GameBadge>
          </S.GameCard>
        </S.GamesGrid>
      </S.Section>

      {/* Floating Action Button */}
      <S.FloatingButton
        $isVisible={isGamesSectionVisible}
        onClick={handleFloatingButtonClick}
        aria-label="Juega con Forti"
      >
        <S.FloatingButtonRipple2 />
        <S.FloatingButtonRipple />
        <S.ParticleContainer>
          {[...Array(6)].map((_, i) => (
            <S.Particle key={i} $delay={i * 0.3} $angle={i * 60} />
          ))}
        </S.ParticleContainer>
        <S.FloatingButtonImage src={fortiGamerImage} alt="Forti Gamer" />
      </S.FloatingButton>

      {/* Resources Section */}
      <S.ResourcesSectionWrapper>
        <S.SectionTitle>{t('resources.title')}</S.SectionTitle>
        <S.SectionSubtitle>
          {t('resources.subtitle')}
        </S.SectionSubtitle>
        
        <ResourcesSection />
      </S.ResourcesSectionWrapper>

      {/* Links Section */}
      <S.LinksSectionWrapper>
        <S.SectionTitle>{t('links.title')}</S.SectionTitle>
        <S.SectionSubtitle>
          {t('links.subtitle')}
        </S.SectionSubtitle>
        
        <LinksSection />
      </S.LinksSectionWrapper>

      {/* Footer */}
      <S.Footer>
        <S.SectionTitle style={{ color: 'white', marginBottom: '1rem' }}>
          {t('home.followUs')}
        </S.SectionTitle>
        <S.SocialLinks>
          <S.SocialLink 
            href="https://www.instagram.com/fe_en_juego/" 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <FaInstagram />
          </S.SocialLink>
          <S.SocialLink 
            href="https://www.facebook.com/feenjuego" 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FaFacebookF />
          </S.SocialLink>
          <S.SocialLink 
            href="https://www.tiktok.com/@fe_en_juego" 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="TikTok"
          >
            <FaTiktok />
          </S.SocialLink>
          <S.SocialLink 
            href="https://wa.me/59895454544" 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="WhatsApp"
          >
            <FaWhatsapp />
          </S.SocialLink>
        </S.SocialLinks>
        <S.FooterText>
          {t('home.copyright')}
        </S.FooterText>
      </S.Footer>
    </S.PageContainer>
  );
}

export default HomePage; 